import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, MapPin, Package } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Supplier, ROUTE_PATHS } from '@/lib/index';
import { springPresets, hoverLift } from '@/lib/motion';

interface SupplierCardProps {
  supplier: Supplier;
}

export function SupplierCard({ supplier }: SupplierCardProps) {
  const supplierPath = ROUTE_PATHS.SUPPLIER_DETAIL.replace(':id', supplier.id);

  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      transition={springPresets.snappy}
    >
      <Card className="h-full flex flex-col overflow-hidden border-border hover:shadow-lg transition-shadow duration-200">
        <CardHeader className="pb-4">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
              {supplier.logo ? (
                <img
                  src={supplier.logo}
                  alt={`${supplier.name} logo`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Building2 className="w-8 h-8 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-xl mb-1 line-clamp-1">
                {supplier.name}
              </CardTitle>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <MapPin className="w-3.5 h-3.5" />
                <span className="line-clamp-1">{supplier.location}, {supplier.country}</span>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 pb-4">
          <CardDescription className="line-clamp-3 mb-4">
            {supplier.shortDescription}
          </CardDescription>

          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Package className="w-4 h-4" />
            <span>{supplier.products.length} Products</span>
          </div>

          <div className="flex flex-wrap gap-2">
            {supplier.categories.slice(0, 3).map((category, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {category}
              </Badge>
            ))}
            {supplier.categories.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{supplier.categories.length - 3}
              </Badge>
            )}
          </div>

          {supplier.featured && (
            <Badge className="mt-3 bg-accent text-accent-foreground">
              Featured Supplier
            </Badge>
          )}
        </CardContent>

        <CardFooter className="pt-4 border-t border-border">
          <Button asChild className="w-full" variant="default">
            <Link to={supplierPath}>View Profile</Link>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}
